import { motion } from 'framer-motion';
import { Code, Globe, Database, Layers, Cpu, Terminal, Palette } from 'lucide-react';

const TechCard = ({ title, icon: Icon, skills, colorClass, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      className="group relative p-10 bg-[#111928]/40 backdrop-blur-2xl border border-white/5 rounded-[2.5rem] hover:bg-[#111928]/60 transition-all duration-500 hover:border-white/30"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition duration-700 rounded-[2.5rem]" />

      <div className="relative z-10">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 bg-gradient-to-br ${colorClass} shadow-xl shadow-black/20 group-hover:scale-110 transition-transform duration-500`}>
          <Icon className="text-white w-7 h-7" />
        </div>

        <h3 className="text-2xl font-black text-white mb-8 tracking-tighter">{title}</h3>

        <div className="flex flex-wrap gap-2.5">
          {skills.map((skill, idx) => (
            <div
              key={idx}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-xs font-bold tracking-widest uppercase text-white group-hover:border-white/40 group-hover:bg-white/10 transition-all duration-300 cursor-default shadow-sm"
            >
              {skill}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const Tech = () => {

  const techCategories = [
    {
      title: 'Core & Architecture',
      icon: Code,
      colorClass: 'from-white to-gray-400',
      skills: ['C++', 'Java', 'Python', 'JavaScript (ES6+)', 'TypeScript', 'SQL', 'Deep Learning']
    },
    {
      title: 'Web Engineering',
      icon: Globe,
      colorClass: 'from-gray-400 to-white',
      skills: ['React.js', 'Next.js', 'Node.js', 'Express', 'TailwindCSS', 'Redux', 'Web3.js']
    },
    {
      title: 'AI Intelligence',
      icon: Cpu,
      colorClass: 'from-white to-gray-500',
      skills: ['TensorFlow', 'PyTorch', 'LangChain', 'OpenAI API', 'RAG', 'VectorDB', 'Computer Vision']
    },
    {
      title: 'Infrastructure',
      icon: Database,
      colorClass: 'from-gray-500 to-white',
      skills: ['MongoDB', 'PostgreSQL', 'Firebase', 'Azure Cloud', 'Redis', 'Docker', 'Prisma']
    },
    {
      title: 'DevOps & Tools',
      icon: Terminal,
      colorClass: 'from-white to-gray-600',
      skills: ['Git', 'Postman', 'Linux', 'Vercel', 'CI/CD', 'GitHub Actions']
    },
    {
      title: 'Design Systems',
      icon: Palette,
      colorClass: 'from-gray-300 to-white',
      skills: ['Figma', 'UI/UX Design', 'Design Tokens', 'Prototyping']
    }
  ];

  return (
    <div className="min-h-screen bg-[#0b0f19] py-24 px-4 sm:px-6 lg:px-8 flex flex-col justify-center relative overflow-hidden">
      {/* Decorative background blur */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-white/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-center mb-24"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-8">
            <Layers className="h-4 w-4 text-white" />
            <span className="text-white text-[10px] font-black tracking-[0.2em] uppercase">Stack</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-black mb-8 tracking-tighter">
            <span className="text-white">Technical </span>
            <span className="gradient-text">
              Arsenal
            </span>
          </h2>
          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
            A comprehensive suite of technologies engineered to build the next generation of <span className="text-white font-bold">scalable solutions</span>.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {techCategories.map((cat, index) => (
            <TechCard
              key={index}
              {...cat}
              delay={index * 0.1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Tech;